import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ACTIVE_WORKSPACE_COOKIE, clearAllCookies, clearCookie, getCookie, setCookie } from '../helpers/common';
import { createContextHook } from 'use-context-hook';
import Toast from '../components/molecules/Toast';
import authService from '../services/authService';
import userService from '../services/userService';
import teamService from '../services/teamService';
import { useCancellablePromise } from '../helpers/promiseHandler';
import { disconnectSocket } from '../helpers/socketConnection/socketConnection';
import { getReferralCode } from '../helpers/referralStorage';
import { isLinkedinStepPending, rememberLinkedinStep } from '../helpers/linkedinStep';
import { clearQueryCache, markQueryCacheStale } from '../helpers/queryClient';

const context = {};

export const AuthContext = createContextHook(context);

/** The workspace the login token was issued for: the user's default one. */
function tokenWorkspaceId() {
  try {
    const payload = getCookie(process.env.REACT_APP_AI_TOKEN_COOKIE)?.split('.')[1];
    if (!payload) return null;
    const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return json?.workspaceId ?? null;
  } catch {
    return null;
  }
}

export function AuthContextProvider(props) {
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState({});
  const [loading_user, setLoadingUser] = useState(false);
  // Pages wait for this: until the active workspace is known, a request would
  // go out without X-Workspace-Id and read or write the token's workspace.
  const [workspaceReady, setWorkspaceReady] = useState(false);
  const [loading_user_google, setLoadingUserGoogle] = useState(false);
  const [fetch_user, setFetchUser] = useState(false);
  const { cancellablePromise } = useCancellablePromise();
  const [reFetch, setRefetch] = useState(false);
  const [authReady, setAuthReady] = useState(false);
  const [authError, setAuthError] = useState(false);
  const [socketData, setSocketData] = useState(null);

  const [workspaces, setWorkspaces] = useState([]);
  const [selectedWorkspace, setSelectedWorkspace] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [allowedPages, setAllowedPages] = useState([]);

  // Consider user logged in if they have a valid token, regardless of status
  const [isLoggedIn, setIsLoggedIn] = useState(!!getCookie(process.env.REACT_APP_AI_TOKEN_COOKIE));

  const onLogout = async () => {
    function handleCookie(res) {
      if (res) {
        clearAllCookies();
      }
    }
    try {
      if (isLoggedIn) setLoadingUser(true);
      const res = await authService.removeAdminJwt();
      handleCookie(res);
    } catch (ex) {
      clearAllCookies();
      Toast({ type: 'error', message: ex?.message });
    } finally {
      disconnectSocket();
      clearQueryCache();

      // Clear all cookies explicitly
      clearCookie(process.env.REACT_APP_ALLOWED_PAGES_COOKIE);
      clearCookie(process.env.REACT_APP_AI_STATUS_COOKIE);

      rememberLinkedinStep(false);
      setLoadingUser(false);
      setWorkspaceReady(false);
      setIsLoggedIn(false);
      setUser({});
      setWorkspaces([]);
      setSelectedWorkspace(null);
      setPermissions([]);
      setAllowedPages([]);
      setAuthReady(true);
      setAuthError(false);
    }
  };

  /**
   * Corrects one of user-details' page flags (pageData) with what the page
   * itself just found, so the next visit opens on the right screen without
   * asking user-details again.
   */
  const setPageFlag = useCallback((key, value) => {
    setUser(prevUser => {
      const userData = prevUser?.data || prevUser;
      if (!userData || userData.pageData?.[key] === value) return prevUser;
      const updatedData = { ...userData, pageData: { ...(userData.pageData || {}), [key]: value } };
      return prevUser.data ? { ...prevUser, data: updatedData } : updatedData;
    });
  }, []);

  /*
   * Only the newest user-details answer is applied. Two loads can overlap (the
   * one started on sign-in, and the refresh after skipping the LinkedIn step);
   * if the older one landed last it put back the stale account — still owing
   * step 3 — and the guard sent the user straight back to the step.
   */
  const permissionsRequestRef = useRef(0);

  const getPermissions = async (shouldUpdate = false) => {
    const requestId = permissionsRequestRef.current + 1;
    permissionsRequestRef.current = requestId;
    const isLatest = () => permissionsRequestRef.current === requestId;
    if (!shouldUpdate) {
      setLoadingUser(true);
    }
    try {
      const res = await cancellablePromise(authService.getCurrentAdmin());
      if (!isLatest()) return;
      rememberLinkedinStep(isLinkedinStepPending(res));
      setUser(res);
      setIsLoggedIn(true);
      if (res.data.tour_completed) {
        localStorage.setItem('hasSeenTour', 'true');
      }
    } catch (err) {
      // A newer load is under way; its answer decides, not this older failure.
      if (!isLatest()) return;
      setIsLoggedIn(false);
      setAuthError(true);
      Toast({ type: 'error', message: err.message });
    } finally {
      // The newest load clears the loading state when it finishes.
      if (isLatest()) {
        setLoadingUser(false);
        setAuthReady(true);
      }
    }
  };

  const UserPermissions = () => {
    setLoadingUser(true);
    cancellablePromise(teamService.getCurrentUserWorkspace())
      .then(res => {
        const workspaceItems = res?.data?.items || [];
        setWorkspaces(workspaceItems);

        /**
         * Which workspace to land on, in priority order: the one already
         * selected in this session, then the one the user last switched to
         * (survives a reload via the cookie fetchWrapper reads), then
         * whichever the backend lists first. Falling through rather than
         * always preferring the cookie means a workspace the user lost
         * access to since their last visit is never silently retried.
         */
        const storedWorkspaceId = getCookie(ACTIVE_WORKSPACE_COOKIE);
        const defaultWorkspaceId = tokenWorkspaceId();
        // The list is newest first, so [0] is only the last resort after the
        // account's own default workspace.
        const preferred =
          (selectedWorkspace && workspaceItems.find(w => w.id === selectedWorkspace.id)) ||
          (storedWorkspaceId && workspaceItems.find(w => String(w.id) === String(storedWorkspaceId))) ||
          (defaultWorkspaceId && workspaceItems.find(w => String(w.id) === String(defaultWorkspaceId))) ||
          workspaceItems[0];
        if (!preferred) clearCookie(ACTIVE_WORKSPACE_COOKIE);

        if (preferred) {
          setSelectedWorkspace(preferred);
          updateUserPermissions(preferred);
        }

        setWorkspaceReady(true);
        setLoadingUser(false);
      })
      .catch(err => {
        setAllowedPages(['no-permissions']);
        setWorkspaceReady(true);
        setLoadingUser(false);
        Toast({
          type: 'error',
          message: err.message,
        });
      });
  };

  const updateUserPermissions = workspace => {
    if (workspace && workspace.permissionDetails) {
      const permissionDetails = workspace.permissionDetails || [];

      const navRoutes = permissionDetails
        .filter(perm => perm.can.endsWith('.nav'))
        .map(perm => perm.route)
        .filter(Boolean);

      const allRoutes = permissionDetails.map(perm => perm.route).filter(Boolean);

      setAllowedPages(allRoutes);
      setCookie(process.env.REACT_APP_ALLOWED_PAGES_COOKIE, JSON.stringify(allRoutes));
      // Read by fetchWrapper on every request (X-Workspace-Id), so whichever
      // workspace is active here is also whichever workspace's data loads.
      setCookie(ACTIVE_WORKSPACE_COOKIE, workspace.id);

      const canPermission = (permissionDetails || []).map(p => p.can);
      setPermissions(canPermission);

      setUser(prevUser => {
        const userData = prevUser.data || prevUser;

        const updatedData = {
          ...userData,
          permissions: canPermission,
          //TODO: uncommit it in case of need
          // workspaceRole: workspace.myRole,
          workspacePermissionDetails: permissionDetails,
          allowedPages: allRoutes,
          navRoutes,
        };

        if (prevUser.data) {
          return {
            ...prevUser,
            data: updatedData,
          };
        }

        return updatedData;
      });
    }
  };

  useEffect(() => {
    if (isLoggedIn) {
      getPermissions();
      UserPermissions();
    } else {
      setAuthReady(true);
    }

    // The same function has to be removed as was added, or every run left one more listener behind.
    const onFetchAdminRole = () => getPermissions();
    window.addEventListener('FETCH_ADMIN_ROLE', onFetchAdminRole);
    return () => window.removeEventListener('FETCH_ADMIN_ROLE', onFetchAdminRole);
  }, [isLoggedIn, fetch_user, reFetch]);

  const onLogin = async ({ email, password }, navigate) => {
    setLoadingUser(true);
    try {
      // Clear previous user data and cookies before login
      clearCookie(process.env.REACT_APP_ALLOWED_PAGES_COOKIE);
      clearQueryCache();

      setUser({});
      setWorkspaces([]);
      setSelectedWorkspace(null);
      setPermissions([]);
      setAllowedPages([]);

      const res = await authService.login({
        email,
        password,
      });
      if (!res?.data?.token) {
        throw new Error(res?.message);
      }

      const token = res?.data?.token;
      const userData = res?.data?.user;

      setCookie(process.env.REACT_APP_AI_TOKEN_COOKIE, token);
      setCookie(process.env.REACT_APP_AI_STATUS_COOKIE, userData?.status);
      setUser(userData);
      setIsLoggedIn(true);

      if (!userData?.isEmailVerified) {
        localStorage.setItem('email', email);
      }
    } catch ({ message }) {
      setIsLoggedIn(false);
      Toast({ type: 'error', message });
    } finally {
      setLoadingUser(false);
    }
  };

  const onGoogleLogin = async ({ access_token, type, sellerType, action }) => {
    setLoadingUserGoogle(true);
    setLoading(true);
    try {
      // Clear previous user data and cookies before login
      clearCookie(process.env.REACT_APP_ALLOWED_PAGES_COOKIE);
      clearQueryCache();

      setUser({});
      setWorkspaces([]);
      setSelectedWorkspace(null);
      setPermissions([]);
      setAllowedPages([]);

      const referral_code = getReferralCode() || getCookie('referral_code');

      const res = await authService.googleLogin({
        access_token,
        action,
        referral_code,
      });

      if (!res?.data?.token) {
        throw new Error(res?.message);
      }

      const token = res?.data?.token;
      const userData = res?.data;

      // Affiliate tracking for Google sign up
      if (action === 'Register' && window?.promotekit?.refer) {
        try {
          const email = userData?.email || userData?.user?.email;
          const stripeCustomerId = userData?.stripeCustomerId || userData?.user?.stripeCustomerId;

          if (email) {
            if (stripeCustomerId) {
              window.promotekit.refer(email, stripeCustomerId);
            } else {
              window.promotekit.refer(email);
            }
          }
        } catch (trackErr) {
          // eslint-disable-next-line no-console
          console.log('PromoteKit refer tracking failed:', trackErr?.message || trackErr);
        }
      }

      setCookie(process.env.REACT_APP_AI_TOKEN_COOKIE, token);
      setCookie(process.env.REACT_APP_AI_STATUS_COOKIE, userData?.status);

      /**
       * Google proves the address, so these accounts never pass the email
       * confirmation page — which is where /onboard-user (trial credits, and the
       * move off `signup`) happens for everyone else. Do it here instead. It is
       * idempotent, and a failure must not block the sign-in.
       */
      if (userData?.isEmailVerified && userData?.status === 'signup') {
        try {
          await userService.userOnboard({});
        } catch (onboardError) {
          console.error('[google-login] silent onboarding failed:', onboardError?.message);
        }
      }

      setUser(userData);
      setIsLoggedIn(true);
    } catch ({ message }) {
      setIsLoggedIn(false);
      setLoadingUserGoogle(false);
      Toast({ type: 'error', message });
    } finally {
      setLoadingUserGoogle(false);
      setLoading(false);
    }
  };

  /**
   * Keeps the navbar balance in step with a turn that just settled.
   * The session payload is the source until the next full refetch; this only
   * rewrites the matching credit row. `total` (when known) keeps the row's
   * allowance right, so "17.92 of 20" never shows as "17.92 of 17.92".
   */
  const updateCreditRemaining = useCallback((creditType, remaining, total) => {
    const amount = Number(remaining);
    const allowance = Number(total);
    const knownTotal = Number.isFinite(allowance) && allowance > 0 ? allowance : null;
    if (!creditType || !Number.isFinite(amount)) return;

    setUser(prev => {
      if (!prev || typeof prev !== 'object') return prev;
      const hasProfile = Boolean(prev.data || prev.id || prev.email || prev.userCredits);
      if (!hasProfile) return prev;

      const apply = data => {
        if (!data || typeof data !== 'object') return data;
        const list = Array.isArray(data.userCredits) ? data.userCredits : [];
        const index = list.findIndex(item => item?.creditType === creditType);
        const userCredits =
          index === -1
            ? [...list, { creditType, remaining: amount, total: knownTotal ?? amount, used: 0 }]
            : list.map((item, i) =>
                i === index ? { ...item, remaining: amount, ...(knownTotal ? { total: knownTotal } : {}) } : item,
              );
        return { ...data, userCredits };
      };

      if (prev.data && typeof prev.data === 'object') {
        return { ...prev, data: apply(prev.data) };
      }
      return apply(prev);
    });
  }, []);

  const hasPermission = perm => {
    const perms =
      Array.isArray(permissions) && permissions.length > 0
        ? permissions
        : user?.permissions || user?.data?.permissions || [];
    const result = Array.isArray(perms) && perms.includes(perm);
    return result;
  };

  return (
    <AuthContext.Provider
      value={{
        setIsLoggedIn,
        onLogout,
        onLogin,
        // Something changed: mounted pages refetch (through `fetch`), and every
        // other cached page refetches when it is next opened.
        refetch: () => {
          markQueryCacheStale();
          setRefetch(_ => !_);
        },
        fetchUser: () => setFetchUser(() => !fetch_user),
        setLoading,
        hasPermission,
        loading,
        isLoggedIn,
        fetch: reFetch,
        user,
        loading_user,
        workspaceReady,
        onGoogleLogin,
        loading_user_google,
        getPermissions,
        setPageFlag,
        authReady,
        authError,
        setSocketData,

        UserPermissions,
        allowedPages,
        setAllowedPages,
        workspaces,
        setWorkspaces,
        selectedWorkspace,
        setSelectedWorkspace,
        updateUserPermissions,
        updateCreditRemaining,
        permissions,
      }}>
      {props.children}
    </AuthContext.Provider>
  );
}
